import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLocale } from '../context/LocaleContext';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchBlogPosts } from '../store/actions/blogActions';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import AdCarousel from '../components/ads/AdCarousel';
import AdSidebar from '../components/ads/AdSidebar';
import { carouselAds, sidebarAds } from '../data/adData';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 2rem 0;
  line-height: 1.6;
`;

const BlogRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const BlogMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const PostGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BlogSidebar = styled.aside`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 16rem;
    flex-shrink: 0;
  }
`;

const PostCard = styled(motion(Link))`
  display: block;
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  text-decoration: none;
  color: inherit;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCard}, 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const PostTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.5rem 0;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 0.5rem;
`;

const PostExcerpt = styled.p`
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
  line-height: 1.5;
`;

function Blog() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const { posts, loading, error } = useSelector((state) => state.blog);

  useEffect(() => {
    dispatch(fetchBlogPosts({ limit: 20 }));
  }, [dispatch]);

  return (
    <PageLayout>
      <SeoHead title="Blog | CiteMatch" description="Latest updates and insights from the CiteMatch team." />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>
        <Title>Blog</Title>
        <Subtitle>Latest updates and insights from the CiteMatch team.</Subtitle>

        <div style={{ marginBottom: '2rem' }}>
          <AdCarousel ads={carouselAds} />
        </div>

        {error && <p style={{ color: 'var(--crimson)' }}>{error}</p>}
        <BlogRow>
          <BlogMain>
            <PostGrid>
              {loading && posts.length === 0 ? null : posts.map((post, i) => (
                <PostCard
                  key={post.id}
                  to={prependPath(`/blog/${post.slug}`)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <PostMeta>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Calendar size={14} /> {post.date ? new Date(post.date).toLocaleDateString() : ''}
                    </span>
                  </PostMeta>
                  <PostTitle>{post.title}</PostTitle>
                  <PostExcerpt>{post.excerpt}</PostExcerpt>
                </PostCard>
              ))}
            </PostGrid>
          </BlogMain>
          <BlogSidebar aria-label="Sponsored">
            {sidebarAds.map((ad) => (
              <AdSidebar key={ad.id} ad={ad} />
            ))}
          </BlogSidebar>
        </BlogRow>
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default Blog;
